/**
 * Shared browser geolocation helpers used by Search (annonces) and SuppliersSearch.
 * Keeps permission / secure-context handling in one place.
 */

/** @typedef {'unsupported' | 'insecure' | 'denied' | 'timeout' | 'failed'} GeoFailureKind */

/** Default options for silent / background geolocation (home map, etc.). */
export const GEO_POSITION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 15000,
  maximumAge: 60000,
};

/**
 * Geolocation APIs require a secure context (HTTPS) or localhost / 127.0.0.1.
 * Plain HTTP on LAN (e.g. phone testing) must not call navigator.geolocation.
 */
export function isGeolocationSecureContext() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const host = (window.location?.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
}

/** True when the app is embedded (Cursor preview / iframe) — GPS is often blocked. */
export function isGeolocationFramed() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function isTouchPhone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(pointer: coarse)").matches || (navigator.maxTouchPoints || 0) > 1;
}

/**
 * True only when permission is already granted (no prompt).
 * Safari may not support permissions.query for geolocation — then returns false.
 */
export async function isGeolocationAlreadyGranted() {
  try {
    if (!navigator?.permissions?.query) return false;
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state === "granted";
  } catch {
    return false;
  }
}

function mapGeoError(err) {
  if (err?.code === 1) return "denied";
  if (err?.code === 3) return "timeout";
  return "failed";
}

/**
 * Request the current position via navigator.geolocation.
 *
 * IMPORTANT: the first getCurrentPosition() must run synchronously inside the
 * user-gesture stack (click handler). Delaying it in a Promise/async tick
 * causes iOS Safari to skip the permission prompt and fail silently.
 */
export function requestBrowserPosition({ onSuccess, onFailure }) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    onFailure?.("unsupported");
    return;
  }
  if (!isGeolocationSecureContext()) {
    onFailure?.("insecure");
    return;
  }

  const phone = isTouchPhone();
  const primary = phone
    ? { enableHighAccuracy: true, timeout: 25000, maximumAge: 60_000 }
    : { enableHighAccuracy: false, timeout: 15000, maximumAge: 60_000 };
  const fallback = phone
    ? { enableHighAccuracy: false, timeout: 20000, maximumAge: 120_000 }
    : { enableHighAccuracy: true, timeout: 20000, maximumAge: 60_000 };

  // Synchronous call — keeps the user-gesture token on iOS/Safari.
  navigator.geolocation.getCurrentPosition(
    (pos) => onSuccess?.(pos),
    (err) => {
      // Permission denied: do not retry (prompt won't show again).
      if (err?.code === 1) {
        onFailure?.(mapGeoError(err), err);
        return;
      }
      // Timeout / position unavailable → one fallback attempt.
      navigator.geolocation.getCurrentPosition(
        (pos) => onSuccess?.(pos),
        (err2) => onFailure?.(mapGeoError(err2), err2),
        fallback,
      );
    },
    primary,
  );
}

/** Map failure kinds to existing i18n keys under search.* */
export function geoFailureMessageKey(kind) {
  switch (kind) {
    case "unsupported":
      return "search.geo_unsupported";
    case "insecure":
      return "search.geo_insecure";
    case "denied":
      return "search.geo_denied";
    case "timeout":
      return "search.geo_timeout";
    default:
      return "search.geo_failed";
  }
}
